// src/context/OnboardingContext.tsx
import { createContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { getFromSessionStorage, setInSessionStorage } from '@/util/storage'

export const ONBOARDING_STATUS_KEY = 'onboarding-status'
export const ONBOARDING_STEP_KEY = 'onboarding-step'

interface OnboardingContextType {
  isOnboarding: boolean | undefined
  onboardingStep: number
  startOnboarding: () => void
  completeOnboarding: () => void
  changeOnboardingStep: (step: number) => void
}

const OnboardingContext = createContext<OnboardingContextType | null>(null)

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [isOnboarding, setIsOnboarding] = useState<boolean | undefined>(() => {
    const isOnboarding = getFromSessionStorage<boolean>(ONBOARDING_STATUS_KEY) // 3 valid values: true, false, undefined (initial)
    Sentry.logger.debug(Sentry.logger.fmt`Initializing Onboarding Status: ${isOnboarding}`)
    return isOnboarding
  })
  const [onboardingStep, setOnboardingStep] = useState(() => {
    const step = getFromSessionStorage<number>(ONBOARDING_STEP_KEY)
    Sentry.logger.debug(Sentry.logger.fmt`Initializing Onboarding Step: ${step}`)
    return step || 1
  })

  useEffect(() => {
    Sentry.logger.debug(Sentry.logger.fmt`Storing onboarding step: ${onboardingStep}`)
    setInSessionStorage(ONBOARDING_STEP_KEY, onboardingStep)
  }, [onboardingStep])

  useEffect(() => {
    Sentry.logger.debug(Sentry.logger.fmt`Storing onboarding status: ${isOnboarding}`)
    setInSessionStorage(ONBOARDING_STATUS_KEY, isOnboarding)
  }, [isOnboarding])

  const startOnboarding = useCallback(() => {
    Sentry.logger.debug('Starting onboarding')
    setIsOnboarding(true)
  }, [])

  const completeOnboarding = useCallback(() => {
    Sentry.logger.debug('Completing onboarding')
    setIsOnboarding(false)
    setOnboardingStep(1)
  }, [])

  const changeOnboardingStep = useCallback((step: number) => {
    setOnboardingStep(step)
  }, [])

  const stateValues = useMemo(() => {
    return {
      isOnboarding,
      onboardingStep,
    }
  }, [isOnboarding, onboardingStep])
  return (
    <OnboardingContext.Provider
      value={{
        ...stateValues,
        startOnboarding,
        completeOnboarding,
        changeOnboardingStep,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  )
}

export default OnboardingContext
